// Posted receipts: what came in, who counted it, and whether its Shopify items reached Shopify (with
// Retry when Shopify refused). Used on the receiving screen, the count screen and in a PO's Receipts tab: the usual
// table, a receipt with more than one line a group (its header the receipt, opened to show its lines).
import { Camera, RotateCw } from "lucide-react";
import { useState } from "react";
import type { PoLine, Receipt, ReceiptLine } from "../../../../shared/db.gen.ts";
import { REJECT_REASON_LABELS } from "../../../../shared/receiving.ts";
import { ago, personName, qty } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { SHOPIFY_LABELS } from "./lib.ts";

export function ShopifyReceiptBadge({ receipt }: { receipt: Receipt }) {
  if (receipt.shopify_status === "none") return null;
  const tone = receipt.shopify_status === "done" ? "ok" : receipt.shopify_status === "failed" ? "danger" : "warn";
  return (
    <span className="inline-flex items-center gap-1" title={receipt.shopify_error || undefined}>
      <Badge tone={tone}>{SHOPIFY_LABELS[receipt.shopify_status]}</Badge>
      {receipt.shopify_status === "failed" && can("receipt.retry_shopify") && (
        <button
          type="button"
          className="inline-flex h-5 items-center gap-1 rounded px-1 text-[11px] text-accent-text hover:bg-hover"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation(); // not the row's own click (opening its receipt's lines)
            mutate("receipt.retry_shopify", { id: receipt.id });
          }}
        >
          <RotateCw className="size-3" /> Retry
        </button>
      )}
    </span>
  );
}

/** A receipt's line (or, for a receipt with none, the receipt itself). */
type Row = { id: string; receipt: Receipt; line: ReceiptLine | null; pl: PoLine | undefined };

export function ReceiptList({ receipts, showPo = false, limit = 20 }: { receipts: Receipt[]; showPo?: boolean; limit?: number }) {
  const lines = useRows("receipt_line");
  const poLines = useRows("po_line");
  const pos = useRows("po");
  const users = useRows("app_user");
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- the table's View and Columns
  if (!receipts.length) return <div className="px-4 py-3 text-muted">Nothing received yet.</div>;
  const shown = receipts.slice(0, limit);
  const ids = new Set(shown.map((r) => r.id));
  const byReceipt = new Map<string, ReceiptLine[]>();
  for (const l of lines) if (ids.has(l.receipt_id)) byReceipt.set(l.receipt_id, [...(byReceipt.get(l.receipt_id) ?? []), l]);
  const rows = shown.flatMap((r): Row[] => {
    const mine = byReceipt.get(r.id) ?? [];
    return mine.length
      ? mine.map((l) => ({ id: l.id, receipt: r, line: l, pl: poLines.find((p) => p.id === l.po_line_id) }))
      : [{ id: r.id, receipt: r, line: null, pl: undefined }];
  });
  const receiptOf = (rs: Row[]) => rs[0].receipt;
  const good = (rs: Row[]) => rs.reduce((n, r) => n + Number(r.line?.qty_received ?? 0), 0);
  const bad = (rs: Row[]) => rs.reduce((n, r) => n + Number(r.line?.qty_rejected ?? 0), 0);
  const number = (r: Receipt) => (
    <>
      <span className="truncate font-mono text-[12px] font-medium">{r.number}</span>
      {r.closes_po && <span className="shrink-0 text-[12px] text-muted">closed short</span>}
    </>
  );
  const poCell = (r: Receipt) => {
    const po = pos.find((p) => p.id === r.po_id);
    return (
      po && (
        <Link
          to={`/receiving?po=${po.id}`}
          onClick={(e) => e.stopPropagation()}
          className="truncate font-mono text-[12px] text-accent-text hover:underline"
          title={`Open ${po.number}`}
        >
          {po.number}
        </Link>
      )
    );
  };
  /** The receipt's notes, and why Shopify refused it. */
  const receiptNotes = (r: Receipt) => (
    <>
      {r.shopify_status === "failed" && r.shopify_error && (
        <span className="truncate text-danger" title={r.shopify_error}>
          {r.shopify_error}
        </span>
      )}
      {r.notes && (
        <span className="truncate text-muted" title={r.notes}>
          {r.notes}
        </span>
      )}
    </>
  );
  const rejected = (l: ReceiptLine) =>
    `${qty(l.qty_rejected)} ${REJECT_REASON_LABELS[l.reject_reason as keyof typeof REJECT_REASON_LABELS]?.toLowerCase() ?? "rejected"}`;
  const columns: Column<Row>[] = [
    {
      key: "number",
      header: "Receipt",
      width: "minmax(130px,1fr)",
      value: (r) => r.receipt.number,
      cell: (r, { nested }) => !nested && number(r.receipt),
      groupCell: (rs) => number(receiptOf(rs)),
    },
    {
      key: "sku",
      header: "SKU",
      width: "minmax(110px,1fr)",
      value: (r) => r.pl?.sku ?? "",
      cell: (r) => <span className="truncate font-mono text-[12px]">{r.pl?.sku}</span>,
      groupCell: (rs) => <span className="font-normal text-muted">{rs.length} lines</span>,
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(140px,1.5fr)",
      value: (r) => r.pl?.name ?? "",
      cell: (r) => <span className="truncate text-muted">{r.pl?.name}</span>,
    },
    {
      key: "good",
      header: "Good",
      width: "80px",
      align: "right",
      value: (r) => Number(r.line?.qty_received ?? 0),
      cell: (r) => r.line && <span className="font-mono">{qty(r.line.qty_received)}</span>,
      groupCell: (rs) => <span className="font-mono">{qty(good(rs))}</span>,
    },
    {
      key: "rejected",
      header: "Rejected",
      width: "130px",
      align: "right",
      value: (r) => Number(r.line?.qty_rejected ?? 0),
      text: (r) => (r.line && Number(r.line.qty_rejected) > 0 ? rejected(r.line) : ""),
      cell: (r) =>
        r.line &&
        Number(r.line.qty_rejected) > 0 && (
          <span className="truncate text-danger" title={rejected(r.line)}>
            {rejected(r.line)}
          </span>
        ),
      groupCell: (rs) => bad(rs) > 0 && <span className="font-normal text-danger">{qty(bad(rs))} rejected</span>,
    },
    ...(showPo
      ? [
          {
            key: "po",
            header: "PO",
            width: "100px",
            value: (r: Row) => pos.find((p) => p.id === r.receipt.po_id)?.number ?? "",
            cell: (r: Row, { nested }: { nested: boolean }) => !nested && poCell(r.receipt),
            groupCell: (rs: Row[]) => poCell(receiptOf(rs)),
          },
        ]
      : []),
    {
      key: "shopify",
      header: "Shopify",
      width: "150px",
      value: (r) => SHOPIFY_LABELS[r.receipt.shopify_status] ?? "",
      cell: (r, { nested }) => !nested && <ShopifyReceiptBadge receipt={r.receipt} />,
      groupCell: (rs) => <ShopifyReceiptBadge receipt={receiptOf(rs)} />,
    },
    {
      key: "by",
      header: "By",
      width: "120px",
      value: (r) => personName(users, r.receipt.received_by),
      cell: (r, { nested }) => !nested && <span className="truncate text-[12px] text-faint">{personName(users, r.receipt.received_by)}</span>,
      groupCell: (rs) => <span className="truncate text-[12px] font-normal text-faint">{personName(users, receiptOf(rs).received_by)}</span>,
    },
    {
      key: "received_at",
      header: "When",
      width: "110px",
      value: (r) => r.receipt.received_at,
      text: (r) => r.receipt.received_at,
      cell: (r, { nested }) =>
        !nested && (
          <span className="truncate text-[12px] text-faint" title={new Date(r.receipt.received_at).toLocaleString()}>
            {ago(r.receipt.received_at)}
          </span>
        ),
      groupCell: (rs) => (
        <span className="truncate text-[12px] font-normal text-faint" title={new Date(receiptOf(rs).received_at).toLocaleString()}>
          {ago(receiptOf(rs).received_at)}
        </span>
      ),
    },
    {
      key: "notes",
      header: "Notes",
      width: "minmax(140px,1.5fr)",
      value: (r) => [r.line?.notes, r.receipt.notes].filter(Boolean).join(" · "),
      cell: (r, { nested }) => (
        <>
          {r.line?.photo_url && (
            <a
              href={r.line.photo_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 text-accent-text"
              title="Photo"
            >
              <Camera className="size-3.5" />
            </a>
          )}
          {r.line?.notes && (
            <span className="truncate text-faint" title={r.line.notes}>
              {r.line.notes}
            </span>
          )}
          {!nested && receiptNotes(r.receipt)}
        </>
      ),
      groupCell: (rs) => <span className="flex min-w-0 items-center gap-1.5 font-normal">{receiptNotes(receiptOf(rs))}</span>,
    },
  ];
  return (
    <div className="px-4 pb-3">
      <div ref={setSlot} className="flex items-center justify-end gap-1 pb-1 empty:hidden" />
      <DataTable
        fit
        rows={rows}
        columns={columns}
        defaultSort={{ key: "received_at", desc: true }}
        groupBy={(r) => r.receipt.id}
        groupNoun={["receipt", "receipts"]}
        rowNoun={["line", "lines"]}
        exportName={showPo ? "Recently received" : "Receipts"}
        viewsSlot={slot}
        pageFilters={false}
      />
    </div>
  );
}
